import { batch } from '@preact/signals-react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentUserPreferences, updateCurrentUserPreferences } from 'api/userPreferences';
import { useEffect } from 'react';
import {
  mergeOverlayAppearanceDraft,
  OverlayAppearancePreferences,
  parseOverlayAppearancePreferences,
} from 'utils/overlayAppearance';
import {
  clearViewerAppearanceDraft,
  getViewerAppearanceDraft,
  overlayAppearanceProfile,
  viewerAppearanceDrafts,
} from './overlayAppearance';

export const overlayAppearanceQueryKey = (userId?: string) => ['overlay-appearance', userId];

export const useSaveOverlayAppearance = (viewerIndex: number, slideId: string, onSaved?: () => void) => {
  const queryClient = useQueryClient();
  const mutationKey = ['save-overlay-appearance', overlayAppearanceProfile.value.userId];
  const mutation = useMutation({
    mutationKey,
    mutationFn: (submitted: OverlayAppearancePreferences) => {
      const draft = getViewerAppearanceDraft(viewerIndex, slideId);
      const latest = overlayAppearanceProfile.peek().preferences;
      return saveOverlayAppearancePreferences(
        draft ? mergeOverlayAppearanceDraft(latest, draft.baseline, submitted) : submitted
      );
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(overlayAppearanceQueryKey(saved.userId), saved.preferences);
      const draft = getViewerAppearanceDraft(viewerIndex, slideId);
      if (draft) clearViewerAppearanceDraft(viewerIndex, draft.sessionId);
      onSaved?.();
    },
  });
  const pendingSaves = useIsMutating({ mutationKey });
  return { ...mutation, isLoading: mutation.isLoading || pendingSaves > 0 };
};

export const OverlayAppearanceProfileLoader = ({ userId }: { userId?: string }): null => {
  const query = useQuery({
    queryKey: overlayAppearanceQueryKey(userId),
    queryFn: async () => parseOverlayAppearancePreferences((await getCurrentUserPreferences()).overlayAppearance),
    enabled: Boolean(userId),
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    batch(() => {
      if (overlayAppearanceProfile.peek().userId !== userId) {
        viewerAppearanceDrafts.forEach((draft) => {
          draft.value = undefined;
        });
      }
      overlayAppearanceProfile.value = {
        userId,
        status: query.isError ? 'error' : query.isSuccess ? 'ready' : 'loading',
        preferences: query.data ?? { version: 1, overlays: {} },
        error: query.isError ? (query.error as Error) : undefined,
      };
    });
  }, [userId, query.data, query.isError, query.isSuccess, query.error]);
  return null;
};

export const saveOverlayAppearancePreferences = async (preferences: OverlayAppearancePreferences) => {
  const userId = overlayAppearanceProfile.peek().userId;
  if (!userId || overlayAppearanceProfile.peek().status !== 'ready')
    throw new Error('Profile preferences are not loaded.');
  const validated = parseOverlayAppearancePreferences(preferences);
  const response = await updateCurrentUserPreferences({ overlayAppearance: validated });
  if (!response?.overlayAppearance) throw new Error('The server did not confirm saved overlay preferences.');
  const saved = parseOverlayAppearancePreferences(response.overlayAppearance);
  if (overlayAppearanceProfile.peek().userId !== userId)
    throw new Error('The signed-in user changed. Reopen overlay appearance.');
  overlayAppearanceProfile.value = { userId, status: 'ready', preferences: saved };
  return { userId, preferences: saved };
};
